import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { getPreferredUserIdentifier, buildAetUserContext } from '../core/constant/user-context.util';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  baseURL = environment.baseAPIURL;
  constructor( private readonly http  : HttpClient) { }
  getreport(user: any) {
    const url: string = environment.baseAPIURL + '/aetReport/aetDashboard';
    const rb = this.buildNestedRequest(user);
    return this.http
      .post(url, rb, { observe: 'response', responseType: 'blob' })
      .pipe(catchError(this.handleError));
  }
  editOnLoad(eventId) {
    const url: string =
      environment.baseAPIURL + '/aetDashBoard/setupEventEdit/' + eventId;
    return this.http
      .get(url, { responseType: 'json' })
      .pipe(catchError(this.handleError));
  }
  fetchDocs(eventId) {
    const url: string = `${this.baseURL}/aetDashBoard/fetchDocuments?eventId=${eventId}`;
    return this.http
      .get(url, { responseType: 'json' })
      .pipe(catchError(this.handleError));
  }
  downloadDocs(eventId, imageId) {
    const url: string = `${this.baseURL}/aetDashBoard/downloadDocument?eventId=${eventId}&imageId=${imageId}`;
    return this.http
      .get(url, { observe: 'response', responseType: 'blob' })
      .pipe(catchError(this.handleError));
  }
  deleteDocs(eventId, imageId) {
    const url: string = `${this.baseURL}/aetDashBoard/deleteDocument?eventId=${eventId}&imageId=${imageId}`;
    return this.http
      .delete(url, { responseType: 'text' })
      .pipe(catchError(this.handleError));
  }
  deleteRow(eventId, toyotaGUID) {
    const userIdentifier = getPreferredUserIdentifier(toyotaGUID);
  const url: string = `${this.baseURL}/aetDashBoard/deleteEvent?eventId=${eventId}&upn=${userIdentifier}`;
    return this.http
      .delete(url, { responseType: 'text' })
      .pipe(catchError(this.handleError));
  }
  uploadFile(file: File, eventId: any) {
    const formData: FormData = new FormData();
    formData.append('eventId', eventId);
    formData.append('file', file, file.name);
    return this.http
      .post(`${this.baseURL}/aetDashBoard/uploadDocument`, formData, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(catchError(this.handleError));
  }
  uploadDelayedDispatchFile(file: File) {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    return this.http
      .post(`${this.baseURL}/aetDashBoard/uploadDelayedDispatchDocument`, formData, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(catchError(this.handleError));
  }
  updateEvent(requestBody, upn: string) {
    const rb = { event: requestBody, userReqDto: buildAetUserContext() };
  return this.http.post(`${this.baseURL}/aetDashBoard/saveAETEvent`, rb, { responseType: 'text' }).pipe(catchError(this.handleError));
  }
  getAddEventData() {
    return this.http
      .get(`${this.baseURL}/aetDashBoard/addEvents/onLoad`, {
        responseType: 'json',
      })
      .pipe(catchError(this.handleError));
  }
  addEventSave(requestBody: { event: any; userReqDto: any }): Observable<any> {
    return this.http
      .post(`${this.baseURL}/aetDashBoard/createEvent`, requestBody, {
        responseType: 'text',
      })
      .pipe(catchError(this.handleError));
  }
  loadingDropdowns(eventId): Promise<any> {
    return this.http
      .get(`${this.baseURL}/aet/setupResolutionViaEvent?eventid=${eventId}`)
      .pipe(catchError(this.handleError))
      .toPromise();
  }

  loadingDropdownsforEdit(resolutionID): Promise<any> {
    return this.http
      .get(
        `${this.baseURL}/aet/setupResolutionWizard?resolutionId=${resolutionID}`
      )
      .pipe(catchError(this.handleError))
      .toPromise();
  }

  saveResolution(object) {
    const requestBody = this.buildNestedRequest(object);
    return this.http
      .post(`${this.baseURL}/aet/aetAddResolutions`, requestBody, {
        responseType: 'text',
      })
      .pipe(catchError(this.handleError));
  }

  updateResolutions(object) {
    const requestBody = this.buildNestedRequest(object);
    return this.http
      .post(`${this.baseURL}/aet/aetEditResolutions`, requestBody, {
        responseType: 'text',
      })
      .pipe(catchError(this.handleError));
  }

  // Ensures outgoing payloads use { event: ..., userReqDto: ... }.
  private buildNestedRequest(body: any): any {
    // If already in nested format, return as-is.
    if (body && body.event && body.userReqDto) {
      return body;
    }
    // Detect if body already contains user context fields (flat payload)
    const userKeys = [
      'userPrincipalName',
      'externalUserId',
      'additionalSupplierCodes',
      'additionalLpCode',
      'additionalNAMC',
      'additionalDomicile',
      'firstName',
      'lastName',
      'email',
      'displayName',
      'roles',
      'isAdmin',
      'userId',
    ];
    const hasUserContext = body && userKeys.some((k) => Object.prototype.hasOwnProperty.call(body, k));
    if (hasUserContext) {
      const userReqDto: any = {};
      const event: any = {};
      Object.keys(body || {}).forEach((k) => {
        if (userKeys.includes(k)) {
          userReqDto[k] = body[k];
        } else {
          event[k] = body[k];
        }
      });
      return { event, userReqDto };
    }
    // Default: attach current user context and send body as event.
    return { event: body, userReqDto: buildAetUserContext() };
  }
  getDashboardGridData(
    toyotaGUID: string, // NOSONAR
    params: {
      domicile: any[];
      namc: any[];
      supplierType: string;
      supplier: any[];
      line: any[];
      dock: any[];
      partNumber: any[];
      lp: any[];
      // status: any[];
      event: any[];
      problem: any[];
      from: string;
      to: string;
      onTime: boolean;
      onlyOnTime: boolean;
    }
  ) {
    const rb = this.buildNestedRequest(params);
    return this.http
      .post(`${this.baseURL}/aetDashBoard/getAetDashboardEvents`, rb, {
        responseType: 'json',
      })
      .pipe(catchError(this.handleError));
  }

  getDashboardGridDataCount(
    toyotaGUID: string, // NOSONAR
    params: {
      domicile: any[];
      namc: any[];
      supplier: any[];
      line: any[];
      dock: any[];
      dateType: string;
      timeZone: string;
      partNumber: any[];
      lp: any[];
      status: any[];
      event: any[];
      problem: any[];
      from: string;
      to: string;
      onTime: boolean;
    }
  ) {
    const rb = this.buildNestedRequest(params);
    return this.http
      .post(`${this.baseURL}/aetDashBoard/getAetDashboardEventsCount`, rb, {
        responseType: 'json',
      })
      .pipe(catchError(this.handleError));
  }

  getOrderNumbers(params: number[]) {
    const rb = this.buildNestedRequest(params);
    return this.http
      .post(`${this.baseURL}/aetDashBoard/getOrders`, rb, {
        responseType: 'json',
      })
      .pipe(catchError(this.handleError));
  }
  getDashboardload(upn) {
    return this.http
      .get(`${this.baseURL}/aetDashBoard/getUserPreference/${upn}`, {
        responseType: 'json',
      })
      .pipe(catchError(this.handleError));
  }
  saveload(insertObject: any, upn: string) {
    console.info('Inside save load method of dashboard service', insertObject);
    const rb = { event: insertObject, userReqDto: buildAetUserContext() };
  return this.http.post(`${this.baseURL}/aetDashBoard/saveUserPreference`, rb, { responseType: 'text' }).pipe(catchError(this.handleError));
  }
  snoozeCall(eventId: any) {
    return this.http.get(
      `${this.baseURL}/aetDashBoard/updateSnooze/${eventId}`,
      { responseType: 'text' }
    )
    .pipe(catchError(this.handleError));
  }

  borderCrossing(routeId: any) {
    return this.http.get(
      `${this.baseURL}/aetDashBoard/borderCrossing/${routeId}`,
      { responseType: 'json' }
    )
    .pipe(catchError(this.handleError));
  }

  saveBorderCrossing(body: any,toyotaGUID: string) {
    const userIdentifier = getPreferredUserIdentifier(toyotaGUID);
    const rb = { event: body, userReqDto: buildAetUserContext() };
  return this.http.post(`${this.baseURL}/aetDashBoard/saveBorderCrossingActualDate`, rb, { responseType: 'text' }).pipe(catchError(this.handleError));
  }

  updateStatus(routeId: any, unloadDateTime: string) {
    const paramsObj = {
      routeId: routeId,
      unloadDateTime: unloadDateTime
    }
    const rb = this.buildNestedRequest(paramsObj);
    return this.http
      .post(`${this.baseURL}/aetDashBoard/setStatusArrived`, rb, { responseType: 'text' })
      .pipe(catchError(this.handleError));
  }

  private handleError(error: HttpErrorResponse) {
    return throwError(() => new Error(error.message || 'Server Error'));
  }
}
